import {TicketMessageEntity} from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import {ChatTicketHeaderEntity} from "@dropDesk/domain/entities/chat/chat_ticket_header.entity";

export class ChatMessagesV2TicketCacheEntity {
    pages!: Record<number, TicketMessageEntity[]>;
    hasMore!: boolean;
    ticket!: ChatTicketHeaderEntity;
    previousTicket!: ChatTicketHeaderEntity | null;
    previousTicketCached!: boolean;
    nextTicket!: ChatTicketHeaderEntity | null;
    nextTicketPage!: number;
    nextTicketCached!: boolean;
    nextTicketResolved!: boolean;

    constructor({
                    pages,
                    hasMore,
                    ticket,
                    previousTicket,
                    previousTicketCached,
                    nextTicket,
                    nextTicketPage,
                    nextTicketCached,
                    nextTicketResolved,
                }: {
        pages: Record<number, TicketMessageEntity[]>;
        hasMore: boolean;
        ticket: ChatTicketHeaderEntity;
        previousTicket: ChatTicketHeaderEntity | null;
        previousTicketCached: boolean;
        nextTicket?: ChatTicketHeaderEntity | null;
        nextTicketPage?: number;
        nextTicketCached?: boolean;
        nextTicketResolved?: boolean;
    }) {
        Object.assign(this, {
            pages,
            hasMore,
            ticket,
            previousTicket,
            previousTicketCached,
            nextTicket: nextTicket ?? null,
            nextTicketPage: nextTicketPage ?? 0,
            nextTicketCached: nextTicketCached === true,
            nextTicketResolved: nextTicketResolved === true,
        });
    }
}

export class ChatMessagesV2SessionEntity {
    tickets!: Record<string, ChatMessagesV2TicketCacheEntity>;
    loadedTicketIds!: string[];
    didAutoLoadPreviousForOpenedTicket!: boolean;
    allowAutoLoadNextAfterNavigate!: boolean;
    didAutoLoadNextAfterNavigate!: boolean;

    constructor({
                    tickets,
                    loadedTicketIds,
                    didAutoLoadPreviousForOpenedTicket,
                    allowAutoLoadNextAfterNavigate,
                    didAutoLoadNextAfterNavigate,
                }: {
        tickets: Record<string, ChatMessagesV2TicketCacheEntity>;
        loadedTicketIds: string[];
        didAutoLoadPreviousForOpenedTicket: boolean;
        allowAutoLoadNextAfterNavigate: boolean;
        didAutoLoadNextAfterNavigate: boolean;
    }) {
        Object.assign(this, {
            tickets,
            loadedTicketIds,
            didAutoLoadPreviousForOpenedTicket,
            allowAutoLoadNextAfterNavigate,
            didAutoLoadNextAfterNavigate,
        });
    }

    public static empty(): ChatMessagesV2SessionEntity {
        return new ChatMessagesV2SessionEntity({
            tickets: {},
            loadedTicketIds: [],
            didAutoLoadPreviousForOpenedTicket: false,
            allowAutoLoadNextAfterNavigate: false,
            didAutoLoadNextAfterNavigate: false,
        });
    }

    public copyWith({
                        tickets,
                        loadedTicketIds,
                        didAutoLoadPreviousForOpenedTicket,
                        allowAutoLoadNextAfterNavigate,
                        didAutoLoadNextAfterNavigate,
                    }: {
        tickets?: Record<string, ChatMessagesV2TicketCacheEntity>;
        loadedTicketIds?: string[];
        didAutoLoadPreviousForOpenedTicket?: boolean;
        allowAutoLoadNextAfterNavigate?: boolean;
        didAutoLoadNextAfterNavigate?: boolean;
    }): ChatMessagesV2SessionEntity {
        return new ChatMessagesV2SessionEntity({
            tickets: tickets ?? this.tickets,
            loadedTicketIds: loadedTicketIds ?? this.loadedTicketIds,
            didAutoLoadPreviousForOpenedTicket: didAutoLoadPreviousForOpenedTicket ?? this.didAutoLoadPreviousForOpenedTicket,
            allowAutoLoadNextAfterNavigate: allowAutoLoadNextAfterNavigate ?? this.allowAutoLoadNextAfterNavigate,
            didAutoLoadNextAfterNavigate: didAutoLoadNextAfterNavigate ?? this.didAutoLoadNextAfterNavigate,
        });
    }
}

export class ChatMessagesV2StateEntity {
    sessions!: Record<string, ChatMessagesV2SessionEntity>;
    listingTicketId!: string | null;
    listingPage!: number;
    listingHasMore!: boolean;
    previousTicket!: ChatTicketHeaderEntity | null;
    previousTicketCached!: boolean;
    listingNewerTicketId!: string | null;
    listingNewerPage!: number;
    listingHasNewer!: boolean;
    nextTicket!: ChatTicketHeaderEntity | null;
    nextTicketPage!: number;
    nextTicketCached!: boolean;
    showLoadPreviousTicketButton!: boolean;
    showLoadNextTicketButton!: boolean;
    listingInFlightKey!: string | null;
    loadingPreviousTicket!: boolean;
    loadingNextTicket!: boolean;

    constructor({
                    sessions,
                    listingTicketId,
                    listingPage,
                    listingHasMore,
                    previousTicket,
                    previousTicketCached,
                    listingNewerTicketId,
                    listingNewerPage,
                    listingHasNewer,
                    nextTicket,
                    nextTicketPage,
                    nextTicketCached,
                    showLoadPreviousTicketButton,
                    showLoadNextTicketButton,
                    listingInFlightKey,
                    loadingPreviousTicket,
                    loadingNextTicket,
                }: {
        sessions: Record<string, ChatMessagesV2SessionEntity>;
        listingTicketId: string | null;
        listingPage: number;
        listingHasMore: boolean;
        previousTicket: ChatTicketHeaderEntity | null;
        previousTicketCached: boolean;
        listingNewerTicketId: string | null;
        listingNewerPage: number;
        listingHasNewer: boolean;
        nextTicket: ChatTicketHeaderEntity | null;
        nextTicketPage: number;
        nextTicketCached: boolean;
        showLoadPreviousTicketButton: boolean;
        showLoadNextTicketButton: boolean;
        listingInFlightKey: string | null;
        loadingPreviousTicket: boolean;
        loadingNextTicket: boolean;
    }) {
        Object.assign(this, {
            sessions,
            listingTicketId,
            listingPage,
            listingHasMore,
            previousTicket,
            previousTicketCached,
            listingNewerTicketId,
            listingNewerPage,
            listingHasNewer,
            nextTicket,
            nextTicketPage,
            nextTicketCached,
            showLoadPreviousTicketButton,
            showLoadNextTicketButton,
            listingInFlightKey,
            loadingPreviousTicket,
            loadingNextTicket,
        });
    }

    public static empty(): ChatMessagesV2StateEntity {
        return new ChatMessagesV2StateEntity({
            sessions: {},
            listingTicketId: null,
            listingPage: 0,
            listingHasMore: false,
            previousTicket: null,
            previousTicketCached: false,
            listingNewerTicketId: null,
            listingNewerPage: 0,
            listingHasNewer: false,
            nextTicket: null,
            nextTicketPage: 0,
            nextTicketCached: false,
            showLoadPreviousTicketButton: false,
            showLoadNextTicketButton: false,
            listingInFlightKey: null,
            loadingPreviousTicket: false,
            loadingNextTicket: false,
        });
    }

    public copyWith({
                        sessions,
                        listingTicketId,
                        listingPage,
                        listingHasMore,
                        previousTicket,
                        previousTicketCached,
                        listingNewerTicketId,
                        listingNewerPage,
                        listingHasNewer,
                        nextTicket,
                        nextTicketPage,
                        nextTicketCached,
                        showLoadPreviousTicketButton,
                        showLoadNextTicketButton,
                        listingInFlightKey,
                        loadingPreviousTicket,
                        loadingNextTicket,
                    }: {
        sessions?: Record<string, ChatMessagesV2SessionEntity>;
        listingTicketId?: string | null;
        listingPage?: number;
        listingHasMore?: boolean;
        previousTicket?: ChatTicketHeaderEntity | null;
        previousTicketCached?: boolean;
        listingNewerTicketId?: string | null;
        listingNewerPage?: number;
        listingHasNewer?: boolean;
        nextTicket?: ChatTicketHeaderEntity | null;
        nextTicketPage?: number;
        nextTicketCached?: boolean;
        showLoadPreviousTicketButton?: boolean;
        showLoadNextTicketButton?: boolean;
        listingInFlightKey?: string | null;
        loadingPreviousTicket?: boolean;
        loadingNextTicket?: boolean;
    }): ChatMessagesV2StateEntity {
        return new ChatMessagesV2StateEntity({
            sessions: sessions ?? this.sessions,
            listingTicketId: listingTicketId !== undefined ? listingTicketId : this.listingTicketId,
            listingPage: listingPage ?? this.listingPage,
            listingHasMore: listingHasMore ?? this.listingHasMore,
            previousTicket: previousTicket !== undefined ? previousTicket : this.previousTicket,
            previousTicketCached: previousTicketCached ?? this.previousTicketCached,
            listingNewerTicketId: listingNewerTicketId !== undefined ? listingNewerTicketId : this.listingNewerTicketId,
            listingNewerPage: listingNewerPage ?? this.listingNewerPage,
            listingHasNewer: listingHasNewer ?? this.listingHasNewer,
            nextTicket: nextTicket !== undefined ? nextTicket : this.nextTicket,
            nextTicketPage: nextTicketPage ?? this.nextTicketPage,
            nextTicketCached: nextTicketCached ?? this.nextTicketCached,
            showLoadPreviousTicketButton: showLoadPreviousTicketButton ?? this.showLoadPreviousTicketButton,
            showLoadNextTicketButton: showLoadNextTicketButton ?? this.showLoadNextTicketButton,
            listingInFlightKey: listingInFlightKey !== undefined ? listingInFlightKey : this.listingInFlightKey,
            loadingPreviousTicket: loadingPreviousTicket ?? this.loadingPreviousTicket,
            loadingNextTicket: loadingNextTicket ?? this.loadingNextTicket,
        });
    }
}
